import React, { useState, FC } from 'react';
import { 
  HiOutlineBell, 
  HiOutlineCheckCircle, 
  HiOutlineInformationCircle, 
  HiOutlineExclamationCircle, 
  HiOutlineTrash,
  HiOutlineClock,
  HiOutlineCheck
} from 'react-icons/hi2';

import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';

interface NotificationProps {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  category: 'system' | 'booking' | 'user';
  time: string;
  isRead: boolean;
}

const MOCK_NOTIFICATIONS: NotificationProps[] = [
  {
    id: '1',
    title: 'New Booking Received',
    message: 'A new safari booking has been made for the "Highlands Adventure" tour.',
    type: 'success',
    category: 'booking',
    time: '2 hours ago',
    isRead: false,
  },
  {
    id: '2',
    title: 'System Update',
    message: 'The admin dashboard will undergo maintenance tonight at 2:00 AM UTC.',
    type: 'info',
    category: 'system',
    time: '5 hours ago',
    isRead: false,
  },
  {
    id: '3',
    title: 'Low Stock Alert',
    message: 'Available seats for the "Lake Nakuru Day Trip" are running low.',
    type: 'warning',
    category: 'system',
    time: '1 day ago',
    isRead: true,
  },
  {
    id: '4',
    title: 'Account Verification',
    message: 'A new agent has requested account verification.',
    type: 'info',
    category: 'user',
    time: '2 days ago',
    isRead: true,
  },
];

const NotificationsPage: FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'system' | 'booking'>('all');
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !n.isRead;
    return n.category === activeTab;
  });

  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n => n.id === id ? { ...n, isRead: true } : n));
  };

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  const getTypeStyles = (type: string) => {
    switch (type) {
      case 'success': return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400';
      case 'warning': return 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400';
      case 'error': return 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400';
      default: return 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <HiOutlineCheckCircle className='w-5 h-5' />;
      case 'warning': return <HiOutlineExclamationCircle className='w-5 h-5' />;
      case 'error': return <HiOutlineExclamationCircle className='w-5 h-5' />;
      default: return <HiOutlineInformationCircle className='w-5 h-5' />;
    }
  };

  return (
    <div className='w-full space-y-6 pt-4 pb-10'>

      
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Notifications
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 font-medium'>
            Stay updated with your latest alerts and activities
          </p>
        </div>

        <div className='flex items-center gap-3'>
          <Button 
            variant='outline-secondary' 
            onClick={markAllAsRead}
            disabled={!notifications.some(n => !n.isRead)}
            className='!px-4 !py-2.5 text-sm font-semibold'
          >
            Mark all as read
          </Button>
        </div>
      </div>

      <Card variant='glass' className='p-1 border-none bg-gray-100/50 dark:bg-gray-800/50 flex flex-wrap gap-1'>
        {['all', 'unread', 'system', 'booking'].map((tab) => (
          <button
            key={tab}
            type='button'
            onClick={() => setActiveTab(tab as 'all' | 'unread' | 'system' | 'booking')}
            className={`
              flex-1 sm:flex-none px-6 py-2.5 rounded-xl text-sm font-bold capitalize transition-all duration-200
              ${activeTab === tab ? 
                'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }
            `}
          >
            {tab}
          </button>
        ))}
      </Card>

      <div className='space-y-4'>
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => (
            <Card 
              key={notification.id}
              className={`
                p-5 border-none transition-all duration-300 group
                ${notification.isRead ? 
                  'bg-white/40 dark:bg-gray-800/20' 
                  : 'bg-white dark:bg-gray-800 shadow-sm ring-1 ring-primary-500/10'
                }
              `}
            >
              <div className='flex gap-4'>
                <div className={`p-2.5 rounded-2xl flex-shrink-0 h-fit ${getTypeStyles(notification.type)}`}>
                  {getTypeIcon(notification.type)}
                </div>

                <div className='flex-1 min-w-0'>
                  <div className='flex items-center justify-between gap-2 mb-1'>
                    <h3 className={`text-base font-bold transition-colors ${notification.isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {notification.title}
                    </h3>
                    <div className='flex items-center gap-2 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest'>
                      <HiOutlineClock className='w-3 h-3' />
                      {notification.time}
                    </div>
                  </div>
                  <p className={`text-sm leading-relaxed ${notification.isRead ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                    {notification.message}
                  </p>

                  <div className='flex items-center gap-4 mt-4'>
                    {!notification.isRead && (
                      <button 
                        type='button'
                        onClick={() => markAsRead(notification.id)}
                        className='text-[11px] font-extrabold text-primary-600 dark:text-primary-400 uppercase tracking-wider flex items-center gap-1.5 hover:underline'
                      >
                        <HiOutlineCheck className='w-3.5 h-3.5' />
                        Mark as read
                      </button>
                    )}
                    <button 
                      type='button'
                      onClick={() => deleteNotification(notification.id)}
                      className='text-[11px] font-extrabold text-rose-500 dark:text-rose-400 uppercase tracking-wider flex items-center gap-1.5 hover:underline'
                    >
                      <HiOutlineTrash className='w-3.5 h-3.5' />
                      Delete
                    </button>
                  </div>
                </div>

                {!notification.isRead && (
                  <div className='w-2 h-2 rounded-full bg-primary-500 mt-2 shadow-sm shadow-primary-500/50' />
                )}
              </div>
            </Card>
          ))
        ) : (
          <Card className='py-20 flex flex-col items-center justify-center text-center bg-white/50 dark:bg-gray-800/50 border-none'>
            <div className='w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4'>
              <HiOutlineBell className='w-8 h-8 text-gray-400' />
            </div>
            <h3 className='text-lg font-bold text-gray-900 dark:text-white'>No notifications found</h3>
            <p className='text-gray-500 dark:text-gray-400 max-w-xs mx-auto mt-1'>
              All caught up! We'll notify you when something important happens.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
